import { useEffect, useState } from "react";
import api, { fmtErr } from "../../lib/api";

const FIELDS = [
  { key: "brand_name", label: "Brand name", type: "text" },
  { key: "contact_email", label: "Public contact email (reply-to)", type: "email" },
  { key: "notify_email", label: "New-lead notification email", type: "email" },
  { key: "website_url", label: "Website URL (used in report CTA)", type: "url" },
  { key: "price_makeover", label: "Profile Makeover price ($)", type: "number" },
  { key: "price_monthly", label: "Monthly support price ($)", type: "number" },
];

export const SettingsForm = () => {
  const [settings, setSettings] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => setSettings({}));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    setErr("");
    try {
      const { data } = await api.put("/admin/settings", settings);
      setSettings(data);
      setMsg("Settings saved.");
    } catch (e2) {
      setErr(fmtErr(e2));
    } finally {
      setBusy(false);
    }
  };

  if (!settings) return <p className="text-sm text-soot">Loading…</p>;

  return (
    <form onSubmit={save} data-testid="settings-form" className="card-surface max-w-2xl p-8">
      <h2 className="font-heading text-2xl font-extrabold tracking-tight text-ink">Branding & Pricing</h2>
      <p className="mt-2 text-sm text-soot">
        These values feed the PDF report, customer emails, and notifications.
      </p>
      <div className="mt-7 space-y-6">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="field-label" htmlFor={`setting-${f.key}`}>{f.label}</label>
            <input
              id={`setting-${f.key}`}
              data-testid={`setting-${f.key}`}
              type={f.type}
              value={settings[f.key] ?? ""}
              onChange={(e) =>
                setSettings((s) => ({
                  ...s,
                  [f.key]: f.type === "number" ? Number(e.target.value) : e.target.value,
                }))
              }
              className="field-input"
            />
          </div>
        ))}
      </div>

      <div className="mt-8 rounded-2xl border border-amber-300 bg-amber-50 p-5">
        <p className="font-heading text-sm font-bold text-amber-800">
          Paid integrations — off by default. Manual mode (create, edit, import, preview, PDF) never calls them.
        </p>
        {[
          { key: "places_enabled", label: "Google Places API audits", warn: "Uses your Google API key — per-call charges apply on every API audit and live search." },
          { key: "ai_enabled", label: "AI analysis generation", warn: "Uses Emergent LLM credits — one Claude call per API audit." },
        ].map((t) => (
          <label key={t.key} className="mt-4 flex cursor-pointer items-start gap-3" data-testid={`setting-toggle-${t.key}`}>
            <input
              type="checkbox"
              data-testid={`setting-${t.key}`}
              checked={!!settings[t.key]}
              onChange={(e) => setSettings((s) => ({ ...s, [t.key]: e.target.checked }))}
              className="mt-1 h-4 w-4 shrink-0 accent-[#065F46]"
            />
            <span className="text-sm">
              <span className="font-semibold text-ink">{t.label}</span>
              <br />
              <span className="text-soot">{t.warn}</span>
            </span>
          </label>
        ))}
      </div>
      {msg && <p data-testid="settings-saved" className="mt-5 rounded-2xl bg-pine-light p-3.5 text-sm font-medium text-pine">{msg}</p>}
      {err && <p data-testid="settings-error" role="alert" className="mt-5 rounded-2xl bg-red-50 p-3.5 text-sm font-medium text-red-700">{err}</p>}
      <button type="submit" disabled={busy} data-testid="settings-save-button" className="btn-primary mt-8 disabled:opacity-60">
        {busy ? "Saving…" : "Save settings"}
      </button>
    </form>
  );
};
